import { Injectable, Type } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

export interface ButtonData {
	text?: string;
	action: (() => void) | (() => Observable<void>) | (() => Promise<any>);
	disabled?: boolean;
	loading?: boolean;
}

export interface ModalData<T = any> {
	open?: boolean;
	title: string;
	message?: string;
	subMessage?: string;
	customComponent?: {
		component: Type<any>;
		inputs: T;
	};
	confirmButton: ButtonData;
	cancelButton: ButtonData;
}

type TypeModalButton = 'confirm' | 'cancel';

@Injectable({
	providedIn: 'root',
})
export class ModalService {
	private defaultConfig: ModalData = {
		open: false,
		title: 'Confirm Action',
		message: 'Are you sure you want to proceed?',
		confirmButton: {
			text: 'Confirm',
			action: () => {},
			disabled: false,
		},
		cancelButton: {
			text: 'Cancel',
			action: () => {
				this.close();
			},
			disabled: false,
		},
	};

	private _config: ModalData = this.defaultConfig;

	public get config(): ModalData {
		return this._config;
	}

	open<T = any>(config: Partial<ModalData<T>>) {
		this._config = { ...this.defaultConfig, ...config, open: true };
		this._config.confirmButton = { ...this.defaultConfig.confirmButton, ...config.confirmButton };
		this._config.cancelButton = { ...this.defaultConfig.cancelButton, ...config.cancelButton };
	}

	close() {
		this._config = this.defaultConfig;
		this._config.open = false;
	}

	async onConfirmAsync() {
		let actionResult: Promise<any>;
		const intermediate = this._config.confirmButton?.action();

		if (intermediate instanceof Observable) {
			actionResult = firstValueFrom(intermediate);
		} else if (intermediate instanceof Promise) {
			actionResult = intermediate;
		} else {
			actionResult = Promise.resolve();
		}

		await actionResult;
		this.close();
	}

	onConfirm() {
		this._config.confirmButton?.action();
		this.close();
	}

	onCancel() {
		this._config.cancelButton?.action();
		this.close();
	}

	disableButton(typeButton: TypeModalButton) {
		const button = this.getButton(this.config, typeButton);
		if (button) {
			button.disabled = true;
		}
	}

	enableButton(typeButton: TypeModalButton) {
		const button = this.getButton(this.config, typeButton);
		if (button) {
			button.disabled = false;
		}
	}

	onLoading() {
		this._config.confirmButton.loading = true;
	}

	onLoaded() {
		this._config.confirmButton.loading = false;
	}

	private getButton(currentConfig: ModalData, typeButton: TypeModalButton) {
		const map: {
			confirm: 'confirmButton';
			cancel: 'cancelButton';
		} = { confirm: 'confirmButton', cancel: 'cancelButton' };

		return currentConfig[map[typeButton]];
	}
}
